import React, { useState, useEffect } from 'react';
import { AddUserOffcanvas } from './add';
import { DeleteUserModal } from './delete';
import { EditUserOffcanvas } from './edit';
import { UsersList } from './list';
import api from '../../libs/axios';
import { useDebounce } from '../../hooks/useDebounce';

// Updated Types based on API response
interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
}

interface User {
  id: number;
  email: string;
  username: string;
  password_hash: string;
  employee_code?: string;
  email_verified: boolean;
  email_verification_token: string | null;
  email_verification_expires_at: string | null;
  phone_number: string | null;
  country_code: string | null;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  gender: string | null;
  profile_picture: string | null;
  address: string | null;
  city: string | null;
  state:
    | string
    | null
    | { id: number; state_name: string; circle_name: string };
  country: string | null;
  pincode: string | null;
  role_id: number;
  association_type: string;
  status: string;
  employment_status: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  state_circle?: string;
  district?: string | { id: number; name: string };
  state_circle_id?: number;
  district_id?: number;
  reporting_latitude?: string | null;
  reporting_longitude?: string | null;
  project_id?: number | null;
  user_projects?: UserProject[];
  project_ids?: number[];
  department_id?: number | null;
  designation_id?: number | null;
  project_manager_id?: number | null;
  role: {
    id: number;
    name: string;
  };
  department?: {
    id: number;
    name: string;
  } | null;
  designation?: {
    id: number;
    name: string;
  };
}

interface Role {
  id: number;
  name: string;
}

// Updated Pagination interface to match API response
interface Pagination {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface UserFormData {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  password: string;
  role_id: string;
  phone_number?: string | null;
  country_code?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  project_ids?: number[];
  department_id?: number | null;
  status: 'initiated' | 'pending' | 'verified' | 'suspended' | 'deactivated';
}

interface EditUserFormData {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  password?: string;
  role_id: string;
  phone_number?: string | null;
  country_code?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  project_ids?: number[];
  department_id?: number | null;
  status: 'initiated' | 'pending' | 'verified' | 'suspended' | 'deactivated';
}

interface UsersManagementProps {
  listEndpoint?: string;
  title?: string;
}

// Main Component
export const UsersManagement: React.FC<UsersManagementProps> = ({
  listEndpoint = '/users/list',
  title = 'User Management',
}) => {
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  });

  // Loading states
  const [usersLoading, setUsersLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal/Offcanvas states
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Search and sort states
  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [currentSort, setCurrentSort] = useState<{
    sort_by: string;
    sort_order: 'ASC' | 'DESC';
  }>({
    sort_by: 'first_name',
    sort_order: 'ASC',
  });

  // Debounce the search field inside filters so the API isn't hit on every keystroke
  const rawSearch: string = currentFilters?.search ?? '';
  const debouncedSearch = useDebounce(rawSearch, 400);
  // Stable filters object with the debounced search value applied
  const debouncedFilters = {
    ...currentFilters,
    ...(debouncedSearch ? { search: debouncedSearch } : { search: undefined }),
  };

  // API Functions
  const fetchUsers = async (
    filters: any = currentFilters,
    sort: any = currentSort,
    page: number = pagination.page,
    limitValue?: number,
  ) => {
    setUsersLoading(true);
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10;

      const queryParams: any = {
        page,
        limit: effectiveLimit,
        ...filters,
        ...sort,
      };

      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key];
        } else {
          queryParams[key] = queryParams[key].toString();
        }
      });

      const response = await api.get(listEndpoint, {
        params: queryParams,
      });

      if (response.data.success) {
        setUsers(response.data.data.users || []);
        setPagination((prev) => response.data.data.pagination || prev);
      } else {
        console.error('API returned success: false');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchRoles = async (search: string = '') => {
    setRolesLoading(true);
    try {
      const params: any = {};
      if (search) {
        params.search = search;
      }

      const response = await api.get('/roles/name', {
        params,
      });

      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Handle error
    } finally {
      setRolesLoading(false);
    }
  };

  const createUser = async (userData: UserFormData) => {
    setActionLoading(true);
    try {
      const response = await api.post('/users/create', userData);

      if (response.data.success) {
        // Refresh users list
        await fetchUsers(currentFilters, currentSort, pagination.page);
        setShowAddUser(false);

        // Show success message
        console.log('User created successfully');
      } else {
        console.error('Failed to create user:', response.data.message);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      // Handle error
    } finally {
      setActionLoading(false);
    }
  };

  const updateUser = async (userId: string, userData: EditUserFormData) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/users/${userId}`, userData);

      if (response.data.success) {
        // Refresh users list
        await fetchUsers(currentFilters, currentSort, pagination.page);
        setShowEditUser(false);
        setSelectedUser(null);

        // Show success message
        console.log('User updated successfully');
      } else {
        console.error('Failed to update user:', response.data.message);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      // Handle error
    } finally {
      setActionLoading(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const response = await api.delete(`/users/${selectedUser.id}`);

      if (response.data.success) {
        // Refresh users list
        await fetchUsers(currentFilters, currentSort, pagination.page);
        setShowDeleteModal(false);
        setSelectedUser(null);

        // Show success message
        console.log('User deleted successfully');
      } else {
        console.error('Failed to delete user:', response.data.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      // Handle error
    } finally {
      setActionLoading(false);
    }
  };

  // Event handlers

  const handleSearch = (filters: any) => {
    setCurrentFilters(filters);
    // If the search field changed, the debouncedSearch effect will fire the API call.
    // For non-search filter changes (role, status, etc.) fire immediately.
    const incomingSearch: string = filters?.search ?? '';
    if (incomingSearch === rawSearch) {
      // Non-search filter changed — fire immediately
      fetchUsers(filters, currentSort, 1);
    }
    // Otherwise the debouncedSearch useEffect below handles it
  };

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchUsers(debouncedFilters, currentSort, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    const sort = { sort_by: sortBy, sort_order: sortOrder };
    setCurrentSort(sort);
    fetchUsers(currentFilters, sort, pagination.page);
  };

  const handlePageChange = (page: number) => {
    fetchUsers(currentFilters, currentSort, page);
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    fetchUsers(currentFilters, currentSort, 1, limit);
  };

  const handleAddUser = () => {
    setShowAddUser(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditUser(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowAddUser(false);
    setShowEditUser(false);
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  return (
    <div className=''>
      <div className=''>
        {/* Main Users List */}
        <UsersList
          users={users}
          pagination={pagination}
          roles={roles}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onAddUser={handleAddUser}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          loading={usersLoading}
          appliedFilters={currentFilters}
          sortState={currentSort}
          title={title}
        />

        {/* Add User Offcanvas - FIXED: Pass createUser function directly */}

        <AddUserOffcanvas
          isOpen={showAddUser}
          onClose={handleCloseModals}
          onSubmit={createUser}
          roles={roles}
          loading={actionLoading}
        />

        {/* Edit User Offcanvas */}
        <EditUserOffcanvas
          isOpen={showEditUser}
          onClose={handleCloseModals}
          onSubmit={updateUser}
          user={selectedUser}
          roles={roles}
          loading={actionLoading}
        />

        {/* Delete User Modal */}
        <DeleteUserModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onDelete={deleteUser}
          text={
            selectedUser
              ? `Are you sure you want to delete "${selectedUser.first_name} ${selectedUser.last_name}"? This will permanently remove their account and all associated data from the system.`
              : ''
          }
          loading={actionLoading}
        />
      </div>
    </div>
  );
};
